import type { AchievementI18N } from '../../../services/generated/Api';
import { clampLanguage, useLanguage, useLanguageStore, type Language } from '../../../stores/languageStore';

export type Lang = Language;

/**
 * The person's language, from the store — the only value that tracks their profile.
 *
 * This used to read `document.documentElement.lang`, which the root layout renders once on
 * the server from the `shot_lang` cookie or the Accept-Language header. Nothing updated it
 * afterwards (two readers, zero writers), while the store gets set from
 * `profileData.languageCode` after auth. In the TMA SPA there is no next page load, so the
 * two stayed diverged for the whole session and trophy titles followed the first request's
 * header instead of the person — Russian text under English or Uzbek chrome. #82.
 */
export const pickLang = (): Lang => clampLanguage(useLanguageStore.getState().language);

/** Reactive counterpart: re-renders the tile when the person switches language mid-session. */
export const useAchievementLang = (): Lang => clampLanguage(useLanguage());

export function pickTitle(i18n: AchievementI18N | undefined, lang: Lang = pickLang()): string {
  if (!i18n) return '';
  // Clamped here as well as in pickLang: an out-of-type value can arrive through the `lang`
  // argument, and indexing i18n with it silently returns undefined — which is exactly the
  // Russian-everywhere fallback this issue is about.
  return i18n[clampLanguage(lang)]?.title ?? i18n.ru?.title ?? '';
}

export function pickDescription(i18n: AchievementI18N | undefined, lang: Lang = pickLang()): string {
  if (!i18n) return '';
  return i18n[clampLanguage(lang)]?.description ?? i18n.ru?.description ?? '';
}
